import { isRecord } from "@tellescope/utils";

// Shapes returned by the shop- proxy_read types (staff-only, unconfirmed in @tellescope/schema
// as of this writing). Only the fields this editor actually reads are declared; the raw Stripe
// objects carry many more.

export interface StripeShopPrice {
  id: string;
  active: boolean;
  currency: string;
  unit_amount: number | null;
  type: "one_time" | "recurring";
  // interval_count distinguishes e.g. quarterly (interval: "month", interval_count: 3) from
  // monthly (interval: "month", interval_count: 1) — Stripe has no separate "quarter" interval.
  recurring: { interval: "day" | "week" | "month" | "year"; interval_count: number } | null;
  product: string | StripeShopProduct;
}

export interface StripeShopProduct {
  id: string;
  active: boolean;
  name: string;
  description: string | null;
  images: string[];
  default_price: string | StripeShopPrice | null;
}

export interface StripeShopListResponse<T> {
  data: T[];
  hasMore: boolean;
  nextCursor?: string;
}

const asString = (value: unknown): string | null => (typeof value === "string" ? value : null);
const asNullableString = (value: unknown): string | null | undefined =>
  value === null ? null : typeof value === "string" ? value : undefined;
const asBoolean = (value: unknown): boolean | null => (typeof value === "boolean" ? value : null);
const asNumber = (value: unknown): number | null => (typeof value === "number" ? value : null);
const asStringArray = (value: unknown): string[] | null =>
  Array.isArray(value) && value.every((v) => typeof v === "string") ? value : null;

export const parseStripeShopPrice = (value: unknown): StripeShopPrice | null => {
  if (!isRecord(value)) return null;
  const id = asString(value.id);
  const currency = asString(value.currency);
  const type = value.type === "one_time" || value.type === "recurring" ? value.type : null;
  const active = asBoolean(value.active);
  if (!id || !currency || !type || active === null) return null;

  const unitAmount = value.unit_amount === null ? null : asNumber(value.unit_amount);
  if (unitAmount === undefined) return null;

  let recurring: StripeShopPrice["recurring"] = null;
  if (isRecord(value.recurring)) {
    const interval = value.recurring.interval;
    const intervalCount = asNumber(value.recurring.interval_count) ?? 1;
    if (interval === "day" || interval === "week" || interval === "month" || interval === "year") {
      recurring = { interval, interval_count: intervalCount };
    }
  }

  const product = isRecord(value.product) ? parseStripeShopProduct(value.product) : asString(value.product);
  if (!product) return null;

  return { id, active, currency, unit_amount: unitAmount, type, recurring, product };
};

export const parseStripeShopProduct = (value: unknown): StripeShopProduct | null => {
  if (!isRecord(value)) return null;
  const id = asString(value.id);
  const name = asString(value.name);
  const active = asBoolean(value.active);
  if (!id || !name || active === null) return null;

  const description = asNullableString(value.description);
  if (description === undefined) return null;

  const images = asStringArray(value.images) ?? [];

  let defaultPrice: StripeShopProduct["default_price"] = null;
  if (typeof value.default_price === "string") {
    defaultPrice = value.default_price;
  } else if (isRecord(value.default_price)) {
    defaultPrice = parseStripeShopPrice(value.default_price);
  }

  return { id, active, name, description, images, default_price: defaultPrice };
};

export const parseStripeShopProductList = (value: unknown): StripeShopListResponse<StripeShopProduct> | null => {
  if (!isRecord(value) || !Array.isArray(value.data)) return null;
  const products: StripeShopProduct[] = [];
  for (const raw of value.data) {
    const product = parseStripeShopProduct(raw);
    if (product) products.push(product);
  }
  const hasMore = asBoolean(value.hasMore) ?? false;
  const nextCursor = asString(value.nextCursor) ?? undefined;
  return { data: products, hasMore, nextCursor };
};

export const parseStripeShopPriceList = (value: unknown): StripeShopListResponse<StripeShopPrice> | null => {
  if (!isRecord(value) || !Array.isArray(value.data)) return null;
  const prices: StripeShopPrice[] = [];
  for (const raw of value.data) {
    const price = parseStripeShopPrice(raw);
    if (price) prices.push(price);
  }
  const hasMore = asBoolean(value.hasMore) ?? false;
  const nextCursor = asString(value.nextCursor) ?? undefined;
  return { data: prices, hasMore, nextCursor };
};
